/**
 * Purchase Orders Page
 *
 * List page for purchase orders with filters, actions, and modal form.
 * Supports deep linking for create/edit modal states.
 * @see US-8.14 - Page Liste des Commandes
 */

import { useState, useCallback } from 'react';
import { ShoppingCart, Plus } from 'lucide-react';
import { Button, Skeleton, Pagination, EmptyState } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useModalRoute, usePermission, useCanSeeFinancials } from '@/hooks';
import { formatDateParam } from '@/components/ui';
import {
  usePurchaseOrders,
  useSendPurchaseOrder,
  useCancelPurchaseOrder,
  useDeletePurchaseOrder,
  useDuplicatePurchaseOrder,
} from '../hooks/usePurchaseOrders';
import { OrderCard, OrderFilters, PurchaseOrderFormModal } from '../components';
import type { PurchaseOrderListItem } from '@bstock/shared';

/**
 * Get initial date range (current month)
 */
function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export default function PurchaseOrdersPage() {
  // Permissions
  const canCreate = usePermission('purchase-orders.create');
  const canSend = usePermission('purchase-orders.send');
  const canDelete = usePermission('purchase-orders.delete');
  const canReadSuppliers = usePermission('suppliers.read');
  const showFinancials = useCanSeeFinancials();

  // Modal routing with deep linking
  const modal = useModalRoute('/achats/commandes', {
    paramName: 'orderId',
    createSegment: 'create',
    editSegment: 'edit',
  });

  // Filters state
  const [supplierId, setSupplierId] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>(getInitialDateRange);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Fetch orders
  const { data: ordersData, isLoading } = usePurchaseOrders({
    page,
    limit,
    supplierId: supplierId || undefined,
    status: status || undefined,
    startDate: formatDateParam(dateRange.start) ?? undefined,
    endDate: formatDateParam(dateRange.end) ?? undefined,
    sortBy: 'orderDate',
    sortOrder: 'desc',
  });

  // Mutations
  const sendMutation = useSendPurchaseOrder();
  const cancelMutation = useCancelPurchaseOrder();
  const deleteMutation = useDeletePurchaseOrder();
  const duplicateMutation = useDuplicatePurchaseOrder();

  const orders = ordersData?.data ?? [];
  const pagination = ordersData?.pagination;

  // Handlers
  const handleCreateOrder = useCallback(() => {
    modal.openCreate();
  }, [modal]);

  const handleViewOrder = useCallback(
    (order: PurchaseOrderListItem) => {
      modal.openView(order.id);
    },
    [modal]
  );

  const handleEditOrder = useCallback(
    (order: PurchaseOrderListItem) => {
      modal.openEdit(order.id);
    },
    [modal]
  );

  const handleSendOrder = useCallback(
    async (order: PurchaseOrderListItem) => {
      if (window.confirm(`Voulez-vous envoyer la commande ${order.orderNumber} ?`)) {
        await sendMutation.mutateAsync(order.id);
      }
    },
    [sendMutation]
  );

  const handleCancelOrder = useCallback(
    async (order: PurchaseOrderListItem) => {
      if (window.confirm(`Voulez-vous annuler la commande ${order.orderNumber} ?`)) {
        await cancelMutation.mutateAsync(order.id);
      }
    },
    [cancelMutation]
  );

  const handleDeleteOrder = useCallback(
    async (order: PurchaseOrderListItem) => {
      if (window.confirm(`Voulez-vous supprimer la commande ${order.orderNumber} ?`)) {
        await deleteMutation.mutateAsync(order.id);
      }
    },
    [deleteMutation]
  );

  const [duplicatingOrderId, setDuplicatingOrderId] = useState<string | null>(null);

  const handleDuplicateOrder = useCallback(
    async (order: PurchaseOrderListItem) => {
      if (duplicateMutation.isPending) return;
      setDuplicatingOrderId(order.id);
      try {
        const result = await duplicateMutation.mutateAsync(order.id);
        // Open edit modal for the duplicated order
        modal.openEdit(result.order.id);
      } finally {
        setDuplicatingOrderId(null);
      }
    },
    [duplicateMutation, modal]
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">Commandes</h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gerez vos commandes fournisseur
              </p>
            </div>
          </div>
{canCreate && (
            <Button variant="primary" onClick={handleCreateOrder}>
              <Plus className="w-4 h-4" />
              Nouvelle commande
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <OrderFilters
          supplierId={supplierId}
          status={status}
          dateRange={dateRange}
          onSupplierChange={(v) => {
            setSupplierId(v);
            setPage(1);
          }}
          onStatusChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          onDateRangeChange={(range) => {
            setDateRange(range);
            setPage(1);
          }}
          showSupplierFilter={canReadSuppliers}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Aucune commande trouvee"
            description="Commencez par creer votre premiere commande fournisseur"
            action={canCreate ? {
              label: 'Creer ma premiere commande',
              onClick: handleCreateOrder,
              icon: Plus,
            } : undefined}
          />
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onView={handleViewOrder}
                onEdit={canCreate ? handleEditOrder : undefined}
                onSend={canSend ? handleSendOrder : undefined}
                onCancel={canSend ? handleCancelOrder : undefined}
                onDelete={canDelete ? handleDeleteOrder : undefined}
                onDuplicate={canCreate ? handleDuplicateOrder : undefined}
                isDuplicating={duplicatingOrderId === order.id}
                showFinancials={showFinancials}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Order Form Modal */}
      <PurchaseOrderFormModal
        isOpen={modal.isOpen}
        mode={modal.mode}
        orderId={modal.resourceId}
        onClose={modal.close}
        onOpenEdit={modal.openEdit}
        showFinancials={showFinancials}
      />
    </div>
  );
}
